import React, { useRef } from "react";
import { Drawer, Card, Typography, Button } from "antd";
import { CloseCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { IGetInvoiceDetailsResponse } from "api/requests";

const { Text } = Typography;

interface PrintInvoiceLabelProps {
  open: boolean;
  onClose: () => void;
  invoiceData: IGetInvoiceDetailsResponse | undefined;
}

const PrintInvoiceLabel: React.FC<PrintInvoiceLabelProps> = ({
  open,
  onClose,
  invoiceData,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const company = useSelector((state: any) => state.company);

  const handlePrint = () => {
    if (printRef.current) {
      const printContents = printRef.current.innerHTML;
      const printWindow = window.open("", "_blank", "width=600,height=600");
      if (printWindow) {
        const printHtml = `
          <html>
            <head>
              <title>Print Invoice Label</title>
              <style>
                /* Set page size for label printing */
                @page {
                  size: 80mm 60mm;
                  margin: 0;
                }
                @media print {
                  html, body {
                    margin: 0;
                    padding: 0;
                    width: 100%;
                    height: 100%;
                    transform: scale(0.99);
                  }
                  .printable-label {
                    width: 80mm !important;
                    height: 60mm !important;
                    margin: 0 !important;
                    padding: 8px !important;
                    box-sizing: border-box;
                    overflow: hidden;
                    page-break-after: always;
                    page-break-inside: avoid;
                    font-family: Arial, sans-serif;
                  }
                  /* Override Ant Design Card styles for printing */
                  .ant-card {
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    box-shadow: none !important;
                  }
                }
              </style>
            </head>
            <body>
              ${printContents}
            </body>
          </html>
        `;
        printWindow.document.open();
        (printWindow.document.write as (s: string) => void)(printHtml);
        printWindow.document.close();

        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    }
  };

  const getProductsAndServices = () => {
    // Get all products and services from invoice items
    const items = invoiceData?.invoiceItems
      ?.map((item: any) => {
        if (item.product) {
          return (
            `${item.product.productName} (${item.quantity}x)` +
            (item.product.productGrade
              ? ` (${item.product.productGrade})`
              : "") +
            (item?.product?.productVariantValues?.find(
              (v: any) =>
                v?.productVariantValue?.productVariantType?.name ===
                "Frame Compatibility"
            )
              ? ` Frame: ${
                  item?.product?.productVariantValues?.find(
                    (v: any) =>
                      v?.productVariantValue.productVariantType?.name ===
                      "Frame Compatibility"
                  )?.productVariantValue?.name
                }`
              : "")
          );
        }
        // If it's a service, use description or a generic service label
        return `${item.description || "Service"} (${item.quantity}x)`;
      })
      .filter(Boolean);
    return items && items.length > 0 ? items.join(", ") : "-";
  };

  const renderInvoiceLabel = () => {
    if (!invoiceData) return null;

    return (
      <Card
        className="printable-label"
        style={{
          width: "100%",
          maxWidth: "80mm",
          margin: "10px auto",
          border: "1px solid #d9d9d9",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "58mm",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            fontSize: "12px",
            lineHeight: "1.2",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <Text strong style={{ fontSize: "14px" }}>
              INVOICE LABEL
            </Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Invoice Number:</Text>
            <Text>{invoiceData.invoiceNumber}</Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Customer Name:</Text>
            <Text>{invoiceData.customer?.customerName || "-"}</Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Customer Phone:</Text>
            <Text>{invoiceData.customer?.customerPhone || "-"}</Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Issue Date:</Text>
            <Text>{dayjs(invoiceData.invoiceDate).format("DD/MM/YYYY")}</Text>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Text strong>Items:</Text>
            <Text
              style={{
                fontSize: "10px",
                textAlign: "center",
                maxWidth: "100%",
                wordBreak: "break-word",
              }}
            >
              {getProductsAndServices()}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "8px",
              paddingTop: "4px",
              borderTop: "1px solid #d9d9d9",
            }}
          >
            <Text strong style={{ fontSize: "13px" }}>
              Total Amount:
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {invoiceData.invoiceTotalAmount} {company.currencySymbol}
            </Text>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Drawer
      title="Print Invoice Label"
      placement="bottom"
      height="100%"
      onClose={onClose}
      open={open}
    >
      <div style={{ marginBottom: 20 }}>
        <Text>Preview of the invoice label that will be printed:</Text>
      </div>

      <div
        ref={printRef}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {renderInvoiceLabel()}
      </div>

      <div
        style={{
          position: "fixed",
          bottom: "15px",
          left: "40%",
          background: "white",
          zIndex: 5,
          padding: "10px 20px",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
          borderRadius: "10px",
        }}
      >
        <Button
          icon={<CloseCircleOutlined />}
          style={{ margin: "0 10px" }}
          onClick={onClose}
        />
        <Button
          icon={<PrinterOutlined />}
          style={{ margin: "0 10px" }}
          onClick={handlePrint}
          type="primary"
        />
      </div>
    </Drawer>
  );
};

export default PrintInvoiceLabel;
